import React, { useState, useEffect, useRef, createRef } from "react";
import { useParams } from "react-router-dom";
import * as M from "./styles";
import {
  Box,
  Alert,
  Snackbar,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Chip,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Paper,
  Divider,
  Button,
  Collapse,
  Switch,
  ToggleButton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SortIcon from "@mui/icons-material/Sort";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RestoreIcon from "@mui/icons-material/Restore";
import { CirclesWithBar } from "react-loader-spinner";
import Charts from "./Charts";
import { useGetTableData2 } from "../Plan/tableHooks";
import { differenceInMinutes } from "date-fns";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import { GanttIFframe } from "../AiGantt/styles";
import PLAN_URL from "../../../utils/PLAN_URL";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { useSearchParams, useLocation } from "react-router-dom";
import goToHelpLink from "../../../utils/goToHelpLink";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

dayjs.locale("ko");

function Graph() {
  const { group_code, project_code } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [searchParams] = useSearchParams();
  const containerWidthRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewMode, setViewMode] = useState("graph");
  const [timeUnit, setTimeUnit] = useState("hour");
  const [barWidth, setBarWidth] = useState(50);
  const [chartWidth, setChartWidth] = useState("100%");
  const scrollContainersRef = useRef([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [allSelected, setAllSelected] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [sortOption, setSortOption] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterOptions, setFilterOptions] = useState({
    showTop5Allowed: false,
    showTop5Prohibited: false,
    showAboveBaseLoad: false,
  });
  const [timeRangeFilter, setTimeRangeFilter] = useState({
    startTime: null,
    endTime: null,
    isActive: false,
  });
  const [dailyFilter, setDailyFilter] = useState({
    startDate: "",
    endDate: "",
    isActive: false,
  });
  const [showTimeRangePanel, setShowTimeRangePanel] = useState(false);
  const [showDetailedTimeSettings, setShowDetailedTimeSettings] =
    useState(false);
  const [originalTimeRange, setOriginalTimeRange] = useState({
    start: null,
    end: null,
  });
  const [availableDates, setAvailableDates] = useState([]);
  const { data, isFetching } = useGetTableData2(
    null,
    `/api/gantt/getLoad?group_code=${group_code}&project_code=${project_code}`
  );
  const { machineData } = useGetTableData2(
    null,
    `/api/mch/getAllMachine?group_code=${group_code}&project_code=${project_code}`,
    "machineData"
  );
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      const dateSet = new Set();
      Object.values(data).forEach((loadDataArray) => {
        loadDataArray.forEach((item) => {
          const date = dayjs(item.time).format("YYYY-MM-DD");
          dateSet.add(date);
        });
      });
      const sortedDates = Array.from(dateSet).sort();
      setAvailableDates(sortedDates);
    }
  }, [data]);
  const handleSortChange = (event) => {
    const value = event.target.value;
    if (value === sortOption) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortOption(value);
      setSortDirection(value === "name" ? "asc" : "desc");
    }
  };
  const parseTimeToMinutes = (timeString) => {
    if (timeString === "내용 없음" || !timeString) return 0;
    let totalMinutes = 0;
    const dayMatch = timeString.match(/(\d+)일/);
    const hourMatch = timeString.match(/(\d+)시간/);
    const minuteMatch = timeString.match(/(\d+)분/);
    if (dayMatch) totalMinutes += parseInt(dayMatch[1]) * 24 * 60;
    if (hourMatch) totalMinutes += parseInt(hourMatch[1]) * 60;
    if (minuteMatch) totalMinutes += parseInt(minuteMatch[1]);
    return totalMinutes;
  };
  const parsePercentage = (percentageString) => {
    if (percentageString === "내용 없음" || !percentageString) return 0;
    return parseFloat(percentageString.replace("%", ""));
  };
  const getFilteredMachines = () => {
    const filteredData = getFilteredData();
    if (!filteredData) return [];
    const machineStats = Object.entries(filteredData).map(
      ([machineId, loadDataArray]) => {
        const matchedObj = machineData?.find(
          (content) => content.machine_id === Number(machineId)
        );
        const baseLoad = matchedObj?.base_load ?? null;
        const maxLoad = matchedObj?.max_load ?? null;
        const statistics = calculateLoadStatistics(
          loadDataArray,
          baseLoad,
          maxLoad
        );
        return {
          machineId,
          baseLoad,
          maxLoad,
          loadDataArray,
          statistics,
          allowedMinutes: parseTimeToMinutes(statistics.allowedOverloadTime),
          prohibitedMinutes: parseTimeToMinutes(
            statistics.prohibitedOverloadTime
          ),
        };
      }
    );
    let filteredMachines = [...machineStats];
    if (filterOptions.showAboveBaseLoad) {
      filteredMachines = filteredMachines.filter((machine) => {
        if (machine.baseLoad === null) return false;
        const hasDataAboveBaseLoad = machine.loadDataArray.some(
          (item) => item.value >= machine.baseLoad
        );
        return hasDataAboveBaseLoad;
      });
    }
    let selectedMachineIds = new Set();
    if (filterOptions.showTop5Allowed) {
      const top5Allowed = filteredMachines
        .sort((a, b) => b.allowedMinutes - a.allowedMinutes)
        .slice(0, 5)
        .map((m) => m.machineId);
      top5Allowed.forEach((id) => selectedMachineIds.add(id));
    }
    if (filterOptions.showTop5Prohibited) {
      const top5Prohibited = filteredMachines
        .sort((a, b) => b.prohibitedMinutes - a.prohibitedMinutes)
        .slice(0, 5)
        .map((m) => m.machineId);
      top5Prohibited.forEach((id) => selectedMachineIds.add(id));
    }
    if (!filterOptions.showTop5Allowed && !filterOptions.showTop5Prohibited) {
      return filteredMachines.map((m) => m.machineId);
    }
    return Array.from(selectedMachineIds);
  };
  const getAvailableMachinesForFilter = () => {
    const filteredData = getFilteredData();
    if (!filteredData) return [];
    if (filterOptions.showTop5Allowed || filterOptions.showTop5Prohibited) {
      return getFilteredMachines();
    }
    return Object.keys(filteredData);
  };
  const getMachinesWithAllowedOverload = (startDate, endDate) => {
    if (!data || !machineData) return [];
    const startTime = startDate.toDate();
    const endTime = endDate.toDate();
    const machinesWithOverload = Object.entries(data)
      .filter(([machineId, loadDataArray]) => {
        const matchedObj = machineData.find(
          (content) => content.machine_id === Number(machineId)
        );
        const baseLoad = matchedObj?.base_load ?? null;
        const maxLoad = matchedObj?.max_load ?? null;
        if (baseLoad === null || maxLoad === null) return false;
        const filteredArray = loadDataArray.filter((item) => {
          const itemTime = new Date(item.time);
          return itemTime >= startTime && itemTime <= endTime;
        });
        const hasAllowedOverloadInRange = filteredArray.some((item) => {
          return item.value >= baseLoad && item.value < maxLoad;
        });
        return hasAllowedOverloadInRange;
      })
      .map(([machineId]) => machineId);
    return machinesWithOverload;
  };
  const getSortedMachineEntries = () => {
    const filteredData = getFilteredData();
    if (!filteredData) return [];
    const entries = Object.entries(filteredData)
      .filter(([machineId]) => {
        if (!selectedMachines.includes(machineId)) return false;
        if (filterOptions.showTop5Allowed || filterOptions.showTop5Prohibited) {
          const displayMachines = getFilteredMachines();
          if (!displayMachines.includes(machineId)) return false;
        }
        if (filterOptions.showAboveBaseLoad) {
          const matchedObj = machineData?.find(
            (content) => content.machine_id === Number(machineId)
          );
          const baseLoad = matchedObj?.base_load ?? null;
          if (baseLoad === null) return false;
          const loadDataArray = filteredData[machineId];
          const hasDataAboveBaseLoad = loadDataArray.some(
            (item) => item.value >= baseLoad
          );
          if (!hasDataAboveBaseLoad) return false;
        }
        return true;
      })
      .map(([machineId, loadDataArray]) => {
        const matchedObj = machineData?.find(
          (content) => content.machine_id === Number(machineId)
        );
        const machineName = matchedObj?.machine_name ?? `장비 ${machineId}`;
        const baseLoad = matchedObj?.base_load ?? null;
        const maxLoad = matchedObj?.max_load ?? null;
        const statistics = calculateLoadStatistics(
          loadDataArray,
          baseLoad,
          maxLoad
        );
        return {
          machineId,
          loadDataArray,
          machineName,
          baseLoad,
          maxLoad,
          statistics,
          totalOperationMinutes: parseTimeToMinutes(
            statistics.totalOperationTime
          ),
          allowedOverloadMinutes: parseTimeToMinutes(
            statistics.allowedOverloadTime
          ),
          prohibitedOverloadMinutes: parseTimeToMinutes(
            statistics.prohibitedOverloadTime
          ),
          overloadRatioValue: parsePercentage(statistics.overloadRatio),
        };
      });
    entries.sort((a, b) => {
      let comparison = 0;
      switch (sortOption) {
        case "name":
          comparison = a.machineName.localeCompare(b.machineName);
          break;
        case "totalOperation":
          comparison = a.totalOperationMinutes - b.totalOperationMinutes;
          break;
        case "allowedOverload":
          comparison = a.allowedOverloadMinutes - b.allowedOverloadMinutes;
          break;
        case "prohibitedOverload":
          comparison =
            a.prohibitedOverloadMinutes - b.prohibitedOverloadMinutes;
          break;
        case "overloadRatio":
          comparison = a.overloadRatioValue - b.overloadRatioValue;
          break;
        default:
          comparison = 0;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return entries;
  };
  const getSortDisplayText = () => {
    const directionText = sortDirection === "asc" ? "↑" : "↓";
    switch (sortOption) {
      case "name":
        return `이름순 ${directionText}`;
      case "totalOperation":
        return `총 가동시간 ${directionText}`;
      case "allowedOverload":
        return `허용시간 ${directionText}`;
      case "prohibitedOverload":
        return `불가시간 ${directionText}`;
      case "overloadRatio":
        return `과부하비율 ${directionText}`;
      default:
        return "정렬 없음";
    }
  };
  const getTimeRangeDisplayText = () => {
    if (dailyFilter.isActive && dailyFilter.startDate && dailyFilter.endDate) {
      const startDate = dayjs(dailyFilter.startDate).format("M월D일");
      const endDate = dayjs(dailyFilter.endDate).format("M월D일");
      if (dailyFilter.startDate === dailyFilter.endDate) {
        return startDate;
      }
      return `${startDate} ~ ${endDate}`;
    }
    if (
      timeRangeFilter.isActive &&
      timeRangeFilter.startTime &&
      timeRangeFilter.endTime
    ) {
      const startDate = timeRangeFilter.startTime.format("M월D일");
      const endDate = timeRangeFilter.endTime.format("M월D일");
      if (
        timeRangeFilter.startTime.format("YYYY-MM-DD") ===
        timeRangeFilter.endTime.format("YYYY-MM-DD")
      ) {
        return startDate;
      }
      return `${startDate} ~ ${endDate}`;
    }

    return "시간 범위 설정";
  };
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      let earliestTime = Infinity;
      let latestTime = -Infinity;
      Object.values(data).forEach((loadDataArray) => {
        if (loadDataArray.length > 0) {
          const startTime = new Date(loadDataArray[0].time).getTime();
          const endTime = new Date(
            loadDataArray[loadDataArray.length - 1].time
          ).getTime();
          earliestTime = Math.min(earliestTime, startTime);
          latestTime = Math.max(latestTime, endTime);
        }
      });
      if (earliestTime !== Infinity && latestTime !== -Infinity) {
        setOriginalTimeRange({
          start: dayjs(earliestTime),
          end: dayjs(latestTime),
        });
        const dateParam = searchParams.get("date");
        if (dateParam && isInitialLoad && machineData) {
          const startTime = dayjs(dateParam).startOf("day");
          const endTime = dayjs(dateParam).add(1, "day").startOf("day");
          setTimeRangeFilter({
            startTime: startTime,
            endTime: endTime,
            isActive: true,
          });
          const machinesWithOverload = getMachinesWithAllowedOverload(
            startTime,
            endTime
          );
          if (machinesWithOverload.length > 0) {
            setSelectedMachines(machinesWithOverload);
            setAllSelected(false);
            setShowFilterPanel(true);
            openSnackbar(
              `${dateParam} 날짜에 ${machinesWithOverload.length}개의 과부하 발생 장비를 표시합니다.`,
              "info"
            );
          } else {
            const allMachineIds = Object.keys(data);
            setSelectedMachines(allMachineIds);
            setAllSelected(true);
            openSnackbar(
              `${dateParam} 날짜에 과부하가 발생한 장비가 없습니다.`,
              "warning"
            );
          }
          setIsInitialLoad(false);
        } else if (!dateParam) {
          setTimeRangeFilter({
            startTime: dayjs(earliestTime),
            endTime: dayjs(latestTime),
            isActive: false,
          });
        }
      }
    }
  }, [data, searchParams, isInitialLoad, machineData]);
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .chart-scroll-container::-webkit-scrollbar {
        height: 8px;
      }
      .chart-scroll-container::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      .chart-scroll-container::-webkit-scrollbar-thumb {
        background: #918fb5;
        border-radius: 4px;
      }
      .chart-scroll-container::-webkit-scrollbar-thumb:hover {
        background: #7a7894;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      const machinesParam = searchParams.get("machines");
      if (machinesParam && isInitialLoad) {
        const machineIds = machinesParam.split(",").map((id) => id.trim());
        setSelectedMachines(machineIds);
        setAllSelected(false);
        setShowFilterPanel(true);
      } else if (!machinesParam && !searchParams.get("date")) {
        const allMachineIds = Object.keys(data);
        setSelectedMachines(allMachineIds);
        setAllSelected(true);
      }
    }
  }, [data, searchParams, isInitialLoad]);
  useEffect(() => {
    const availableMachines = getAvailableMachinesForFilter();
    if (filterOptions.showTop5Allowed || filterOptions.showTop5Prohibited) {
      setSelectedMachines(availableMachines);
      setAllSelected(true);
    } else {
      const filteredData = getFilteredData();
      if (filteredData && !searchParams.get("date")) {
        const allMachineIds = Object.keys(filteredData);
        setSelectedMachines(allMachineIds);
        setAllSelected(true);
      }
    }
  }, [
    filterOptions.showTop5Allowed,
    filterOptions.showTop5Prohibited,
    data,
    timeRangeFilter,
    dailyFilter,
    machineData,
  ]);
  const getFilteredData = () => {
    if (!data) return data;
    if (dailyFilter.isActive && dailyFilter.startDate && dailyFilter.endDate) {
      const startTime = dayjs(dailyFilter.startDate).startOf("day").toDate();
      const endTime = dayjs(dailyFilter.endDate).endOf("day").toDate();
      const filteredData = {};
      Object.entries(data).forEach(([machineId, loadDataArray]) => {
        const filteredArray = loadDataArray.filter((item) => {
          const itemTime = new Date(item.time);
          return itemTime >= startTime && itemTime <= endTime;
        });
        if (filteredArray.length > 0) {
          filteredData[machineId] = filteredArray;
        }
      });
      return filteredData;
    }
    if (timeRangeFilter.isActive) {
      const filteredData = {};
      const startTime = timeRangeFilter.startTime.toDate();
      const endTime = timeRangeFilter.endTime.toDate();
      Object.entries(data).forEach(([machineId, loadDataArray]) => {
        const filteredArray = loadDataArray.filter((item) => {
          const itemTime = new Date(item.time);
          return itemTime >= startTime && itemTime <= endTime;
        });
        if (filteredArray.length > 0) {
          filteredData[machineId] = filteredArray;
        }
      });
      return filteredData;
    }
    return data;
  };
  const handleFilterToggle = (filterType) => {
    setFilterOptions((prev) => ({
      ...prev,
      [filterType]: !prev[filterType],
    }));
  };
  const applyTimeRange = () => {
    if (!timeRangeFilter.startTime || !timeRangeFilter.endTime) {
      openSnackbar("시작 시간과 종료 시간을 모두 설정해주세요.", "warning");
      return;
    }
    if (timeRangeFilter.startTime.isAfter(timeRangeFilter.endTime)) {
      openSnackbar("시작 시간이 종료 시간보다 늦을 수 없습니다.", "error");
      return;
    }
    setTimeRangeFilter((prev) => ({
      ...prev,
      isActive: true,
    }));
    setDailyFilter((prev) => ({
      ...prev,
      isActive: false,
    }));
    openSnackbar("시간 범위가 적용되었습니다.", "success");
  };
  const applyDailyFilter = () => {
    if (!dailyFilter.startDate || !dailyFilter.endDate) {
      openSnackbar("시작 날짜와 종료 날짜를 모두 설정해주세요.", "warning");
      return;
    }
    if (dayjs(dailyFilter.startDate).isAfter(dayjs(dailyFilter.endDate))) {
      openSnackbar("시작 날짜가 종료 날짜보다 늦을 수 없습니다.", "error");
      return;
    }
    setDailyFilter((prev) => ({
      ...prev,
      isActive: true,
    }));
    setTimeRangeFilter((prev) => ({
      ...prev,
      isActive: false,
    }));
    const dayCount =
      dayjs(dailyFilter.endDate).diff(dayjs(dailyFilter.startDate), "day") + 1;
    openSnackbar(`${dayCount}일 기간의 데이터가 적용되었습니다.`, "success");
  };
  const showFullTimeRange = () => {
    setTimeRangeFilter({
      startTime: originalTimeRange.start,
      endTime: originalTimeRange.end,
      isActive: false,
    });
    setDailyFilter({
      startDate: "",
      endDate: "",
      isActive: false,
    });
    const filteredData = getFilteredData();
    if (filteredData) {
      setSelectedMachines(Object.keys(filteredData));
      setAllSelected(true);
    }
    openSnackbar("전체 시간 범위로 복원되었습니다.", "info");
  };
  const handleViewModeChange = (event) => {
    setViewMode(event.target.checked ? "gantt" : "graph");
  };
  const calculateLoadStatistics = (loadDataArray, baseLoad, maxLoad) => {
    const formatTime = (totalMinutes) => {
      const days = Math.floor(totalMinutes / (24 * 60));
      const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
      const minutes = totalMinutes % 60;
      let result = "";
      if (days > 0) {
        result += `${days}일 `;
      }
      result += `${hours}시간 ${minutes}분`;
      return result;
    };
    if (!loadDataArray || loadDataArray.length === 0) {
      return {
        totalOperationTime: "0시간 0분",
        allowedOverloadTime: "내용 없음",
        prohibitedOverloadTime: "내용 없음",
        overloadRatio: "내용 없음",
      };
    }
    if (baseLoad === null || maxLoad === null) {
      let totalOperationMinutes = 0;
      for (let i = 0; i < loadDataArray.length - 1; i++) {
        if (loadDataArray[i].value > 0) {
          const currentTime = new Date(loadDataArray[i].time);
          const nextTime = new Date(loadDataArray[i + 1].time);
          const duration = differenceInMinutes(nextTime, currentTime);
          totalOperationMinutes += duration;
        }
      }
      return {
        totalOperationTime: formatTime(totalOperationMinutes),
        allowedOverloadTime: "내용 없음",
        prohibitedOverloadTime: "내용 없음",
        overloadRatio: "내용 없음",
      };
    }
    let totalOperationMinutes = 0;
    let allowedOverloadMinutes = 0;
    let prohibitedOverloadMinutes = 0;
    for (let i = 0; i < loadDataArray.length - 1; i++) {
      const currentValue = loadDataArray[i].value;
      if (currentValue > 0) {
        const currentTime = new Date(loadDataArray[i].time);
        const nextTime = new Date(loadDataArray[i + 1].time);
        const duration = differenceInMinutes(nextTime, currentTime);
        totalOperationMinutes += duration;
        if (currentValue >= baseLoad) {
          if (currentValue < maxLoad) {
            allowedOverloadMinutes += duration;
          } else {
            prohibitedOverloadMinutes += duration;
          }
        }
      }
    }
    const lastIndex = loadDataArray.length - 1;
    if (lastIndex >= 0 && loadDataArray[lastIndex].value > 0) {
      const timeUnitInMinutes = getTimeUnitMinutes();
      totalOperationMinutes += timeUnitInMinutes;
      const lastValue = loadDataArray[lastIndex].value;
      if (lastValue >= baseLoad) {
        if (lastValue < maxLoad) {
          allowedOverloadMinutes += timeUnitInMinutes;
        } else {
          prohibitedOverloadMinutes += timeUnitInMinutes;
        }
      }
    }
    const overloadMinutes = allowedOverloadMinutes + prohibitedOverloadMinutes;
    const overloadRatio =
      totalOperationMinutes > 0
        ? ((overloadMinutes / totalOperationMinutes) * 100).toFixed(1)
        : "0";
    return {
      totalOperationTime: formatTime(totalOperationMinutes),
      allowedOverloadTime: formatTime(allowedOverloadMinutes),
      prohibitedOverloadTime: formatTime(prohibitedOverloadMinutes),
      overloadRatio: overloadRatio + "%",
    };
  };
  useEffect(() => {
    const filteredData = getFilteredData();
    if (filteredData) {
      scrollContainersRef.current = Array(Object.keys(filteredData).length)
        .fill()
        .map((_, i) => scrollContainersRef.current[i] || createRef());
    }
  }, [data, timeRangeFilter, dailyFilter]);
  useEffect(() => {
    const filteredData = getFilteredData();
    if (!filteredData || Object.keys(filteredData).length === 0) return;
    setLoading(true);
    let earliestTime = Infinity;
    let latestTime = -Infinity;
    let maxDataPoints = 0;
    Object.values(filteredData).forEach((loadDataArray) => {
      if (loadDataArray.length > 0) {
        const startTime = new Date(loadDataArray[0].time).getTime();
        const endTime = new Date(
          loadDataArray[loadDataArray.length - 1].time
        ).getTime();
        earliestTime = Math.min(earliestTime, startTime);
        latestTime = Math.max(latestTime, endTime);
        maxDataPoints = Math.max(maxDataPoints, loadDataArray.length);
      }
    });
    const currentTimeUnitMinutes = getTimeUnitMinutes();
    const totalTimeRangeMs = latestTime - earliestTime;
    const estimatedTickCount = Math.ceil(
      totalTimeRangeMs / (currentTimeUnitMinutes * 60 * 1000)
    );
    const containerWidth = containerWidthRef.current?.offsetWidth || 1000;
    const idealTickSpacingPx = 80;
    const minimumRequiredWidth = estimatedTickCount * idealTickSpacingPx;
    let baseWidthRatio = minimumRequiredWidth / containerWidth;
    baseWidthRatio = Math.max(1, baseWidthRatio);
    const timeUnitMultiplier = {
      fiveMin: 1.5,
      tenMin: 1.3,
      halfHour: 1.2,
      hour: 1,
      twoHours: 0.9,
      eightHours: 0.8,
      day: 0.7,
    };
    const barWidthRatio = barWidth / 50;
    const finalWidthRatio =
      baseWidthRatio * timeUnitMultiplier[timeUnit] * barWidthRatio;
    const newWidth = `${Math.max(100, 100 * finalWidthRatio)}%`;
    setChartWidth(newWidth);
    setLoading(false);
  }, [timeUnit, barWidth, data, timeRangeFilter, dailyFilter]);
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  useEffect(() => {
    if (isFetching) {
      setLoading(true);
      setLoadingMessage("부하율 데이터를 불러오는 중...");
    } else {
      setLoading(false);
    }
  }, [isFetching]);
  const getTimeUnitMinutes = () => {
    switch (timeUnit) {
      case "halfHour":
        return 30;
      case "hour":
        return 60;
      case "twoHours":
        return 120;
      case "eightHours":
        return 480;
      case "day":
        return 1440;
      default:
        return 60;
    }
  };
  const handleScroll = (event, index) => {
    const scrollLeft = event.target.scrollLeft;
    scrollContainersRef.current.forEach((ref, i) => {
      if (i !== index && ref.current) {
        ref.current.scrollLeft = scrollLeft;
      }
    });
  };
  const toggleMachineSelection = (machineId) => {
    setSelectedMachines((prev) => {
      const isSelected = prev.includes(machineId);
      const newSelection = isSelected
        ? prev.filter((id) => id !== machineId)
        : [...prev, machineId];
      const availableMachines = getAvailableMachinesForFilter();
      setAllSelected(newSelection.length === availableMachines.length);
      return newSelection;
    });
  };
  const toggleAllMachines = () => {
    const availableMachines = getAvailableMachinesForFilter();
    if (allSelected) {
      setSelectedMachines([]);
      setAllSelected(false);
    } else {
      setSelectedMachines(availableMachines);
      setAllSelected(true);
    }
  };
  const getMachineName = (machineId) => {
    const matchedObj = machineData?.find(
      (content) => content.machine_id === Number(machineId)
    );
    return matchedObj?.machine_name ?? `장비 ${machineId}`;
  };
  const shouldDisableDate = (date) => {
    const dateString = date.format("YYYY-MM-DD");
    return !availableDates.includes(dateString);
  };
  const filteredData = getFilteredData();
  const sortedMachineEntries = getSortedMachineEntries();
  const availableMachinesForFilter = getAvailableMachinesForFilter();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <M.Wrapper>
        <M.FrameContainer>
          <M.ToolbarContainer>
            <M.ToolbarLeft>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ fontSize: "22px !important", color: "#111" }}
              >
                {viewMode === "graph" ? "GRAPH" : "GANTT"}
              </Typography>
              <Tooltip
                title="이동"
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  goToHelpLink(
                    "http://esehelp.cafe24.com/planner#h-5-%EA%B7%B8%EB%9E%98%ED%94%84-%ED%8E%98%EC%9D%B4%EC%A7%80"
                  )
                }
              >
                <HelpOutlineIcon />
              </Tooltip>
              {searchParams.get("date") && (
                <Chip
                  label={`📅 ${searchParams.get("date")} 과부하 상태`}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2 }}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={viewMode === "gantt"}
                    onChange={handleViewModeChange}
                    color="primary"
                  />
                }
                label={viewMode === "graph" ? "간트 차트 보기" : "그래프 보기"}
                sx={{ ml: 2 }}
              />
            </M.ToolbarLeft>
            {viewMode === "graph" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <ToggleButton
                    value="top5Allowed"
                    selected={filterOptions.showTop5Allowed}
                    onChange={() => handleFilterToggle("showTop5Allowed")}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      px: 1,
                      height: "32px",
                      backgroundColor: filterOptions.showTop5Allowed
                        ? "#e3f2fd"
                        : "transparent",
                      "&.Mui-selected": {
                        backgroundColor: "#1976d2",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#1565c0",
                        },
                      },
                    }}
                  >
                    초과시간 TOP5
                  </ToggleButton>
                </Box>
                {/* 정렬 옵션 */}
                <FormControl sx={{ minWidth: 160 }} size="small">
                  <InputLabel id="sort-option-label">정렬 기준</InputLabel>
                  <Select
                    labelId="sort-option-label"
                    value={sortOption}
                    label="정렬 기준"
                    onChange={handleSortChange}
                    startAdornment={<SortIcon sx={{ mr: 1, color: "#666" }} />}
                    renderValue={(value) => (
                      <Typography variant="body2" sx={{ fontSize: "12px" }}>
                        {getSortDisplayText()}
                      </Typography>
                    )}
                  >
                    <MenuItem value="name">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2">장비명</Typography>
                        {sortOption === "name" && (
                          <Typography variant="caption" color="primary">
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                    <MenuItem value="totalOperation">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2">총 가동시간</Typography>
                        {sortOption === "totalOperation" && (
                          <Typography variant="caption" color="primary">
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                    <MenuItem value="allowedOverload">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2">허용 과부하시간</Typography>
                        {sortOption === "allowedOverload" && (
                          <Typography variant="caption" color="success.main">
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                    <MenuItem value="prohibitedOverload">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2">불가 과부하시간</Typography>
                        {sortOption === "prohibitedOverload" && (
                          <Typography variant="caption" color="error.main">
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                    <MenuItem value="overloadRatio">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2">
                          과부하 시간 비율
                        </Typography>
                        {sortOption === "overloadRatio" && (
                          <Typography variant="caption" color="warning.main">
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  color="info"
                  startIcon={<AccessTimeIcon />}
                  onClick={() => setShowTimeRangePanel(!showTimeRangePanel)}
                  sx={{
                    height: "40px",
                    // backgroundColor: showTimeRangePanel
                    //   ? "#e3f2fd"
                    //   : "transparent",
                    borderRadius: "6px"
                  }}
                >
                  {getTimeRangeDisplayText()}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  startIcon={<RestoreIcon />}
                  onClick={showFullTimeRange}
                  sx={{ height: "40px", borderRadius: "6px" }}
                >
                  전체 시간 표시
                </Button>
                {dailyFilter.isActive && (
                  <Chip
                    label={`📅 ${dayjs(dailyFilter.startDate).format(
                      "MM/DD"
                    )} ~ ${dayjs(dailyFilter.endDate).format("MM/DD")}`}
                    color="primary"
                    variant="filled"
                    size="small"
                  />
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    showFilterPanel ? <FilterListOffIcon /> : <FilterListIcon />
                  }
                  onClick={() => setShowFilterPanel(!showFilterPanel)}
                  sx={{ height: "40px",  borderRadius: "6px" }}
                >
                  장비 필터 {showFilterPanel ? "숨기기" : "표시"}
                </Button>
                <FormControl sx={{ minWidth: 120 }} size="small">
                  <InputLabel id="time-unit-label">시간 단위</InputLabel>
                  <Select
                    labelId="time-unit-label"
                    id="time-unit-select"
                    value={timeUnit}
                    label="시간 단위"
                    onChange={(e) => setTimeUnit(e.target.value)}
                  >
                    <MenuItem value="halfHour">30분</MenuItem>
                    <MenuItem value="hour">1시간</MenuItem>
                    <MenuItem value="twoHours">2시간</MenuItem>
                    <MenuItem value="eightHours">8시간</MenuItem>
                    <MenuItem value="day">1일</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </M.ToolbarContainer>
          {viewMode === "graph" && (
            <Collapse in={showTimeRangePanel} timeout={300}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Paper
                  elevation={2}
                  sx={{
                    padding: 3,
                    maxWidth: "900px",
                    width: "100%",
                    borderRadius: 2,
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 3, textAlign: "center" }}
                  >
                    📅 시간 범위 설정
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 3,
                        flexWrap: "wrap",
                      }}
                    >
                      <DatePicker
                        label="시작 날짜"
                        value={
                          dailyFilter.startDate
                            ? dayjs(dailyFilter.startDate)
                            : null
                        }
                        onChange={(newValue) => {
                          const dateString = newValue
                            ? newValue.format("YYYY-MM-DD")
                            : "";
                          setDailyFilter((prev) => ({
                            ...prev,
                            startDate: dateString,
                            endDate: dateString,
                          }));
                        }}
                        shouldDisableDate={shouldDisableDate}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { minWidth: 150 },
                          },
                        }}
                      />
                      <DatePicker
                        label="종료 날짜"
                        value={
                          dailyFilter.endDate
                            ? dayjs(dailyFilter.endDate)
                            : null
                        }
                        onChange={(newValue) => {
                          const dateString = newValue
                            ? newValue.format("YYYY-MM-DD")
                            : "";
                          setDailyFilter((prev) => ({
                            ...prev,
                            endDate: dateString,
                          }));
                        }}
                        shouldDisableDate={(date) => {
                          if (!dailyFilter.startDate)
                            return shouldDisableDate(date);
                          const dateString = date.format("YYYY-MM-DD");
                          return (
                            !availableDates.includes(dateString) ||
                            dateString < dailyFilter.startDate
                          );
                        }}
                        disabled={!dailyFilter.startDate}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { minWidth: 150 },
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={applyDailyFilter}
                        disabled={
                          !dailyFilter.startDate || !dailyFilter.endDate
                        }
                        sx={{ height: "40px" }}
                      >
                        일별 적용
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          showDetailedTimeSettings ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )
                        }
                        onClick={() =>
                          setShowDetailedTimeSettings(!showDetailedTimeSettings)
                        }
                        sx={{ height: "40px" }}
                      >
                        자세히 설정하기
                      </Button>
                    </Box>
                  </Box>
                  <Collapse in={showDetailedTimeSettings} timeout={300}>
                    <Divider sx={{ my: 3 }} />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 2,
                          color: "#2e7d32",
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        🕐 시간별 상세 설정
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 3,
                          flexWrap: "wrap",
                        }}
                      >
                        <DatePicker
                          label="시작 날짜"
                          value={timeRangeFilter.startTime}
                          onChange={(value) =>
                            setTimeRangeFilter((prev) => ({
                              ...prev,
                              startTime: value,
                            }))
                          }
                          shouldDisableDate={shouldDisableDate}
                          minDate={originalTimeRange.start}
                          maxDate={originalTimeRange.end}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { minWidth: 150 },
                            },
                          }}
                        />
                        <TimePicker
                          label="시작 시간"
                          value={timeRangeFilter.startTime}
                          onChange={(value) =>
                            setTimeRangeFilter((prev) => ({
                              ...prev,
                              startTime: value,
                            }))
                          }
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { minWidth: 120 },
                            },
                          }}
                        />
                        <DatePicker
                          label="종료 날짜"
                          value={timeRangeFilter.endTime}
                          onChange={(value) =>
                            setTimeRangeFilter((prev) => ({
                              ...prev,
                              endTime: value,
                            }))
                          }
                          shouldDisableDate={shouldDisableDate}
                          minDate={
                            timeRangeFilter.startTime || originalTimeRange.start
                          }
                          maxDate={originalTimeRange.end}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { minWidth: 150 },
                            },
                          }}
                        />
                        <TimePicker
                          label="종료 시간"
                          value={timeRangeFilter.endTime}
                          onChange={(value) =>
                            setTimeRangeFilter((prev) => ({
                              ...prev,
                              endTime: value,
                            }))
                          }
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { minWidth: 120 },
                            },
                          }}
                        />
                        <Button
                          variant="contained"
                          color="success"
                          onClick={applyTimeRange}
                          disabled={
                            !timeRangeFilter.startTime ||
                            !timeRangeFilter.endTime
                          }
                          sx={{ height: "40px" }}
                        >
                          시간 적용
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>
              </Box>
            </Collapse>
          )}
          <Collapse
            in={showFilterPanel}
            timeout={300}
            mountOnEnter
            unmountOnExit
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Paper
                elevation={2}
                sx={{
                  padding: 3,
                  maxWidth: "900px",
                  width: "100%",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    장비 필터
                    {(filterOptions.showTop5Allowed ||
                      filterOptions.showTop5Prohibited) && (
                      <Chip
                        label={`${availableMachinesForFilter.length}개 장비 표시`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        onChange={toggleAllMachines}
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                      />
                    }
                    label="전체 선택"
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <FormGroup
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "center",
                  }}
                >
                  {availableMachinesForFilter.map((machineId) => (
                    <FormControlLabel
                      key={machineId}
                      control={
                        <Checkbox
                          checked={selectedMachines.includes(machineId)}
                          onChange={() => toggleMachineSelection(machineId)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ minWidth: "150px" }}>
                          {getMachineName(machineId)}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        padding: "2px 8px",
                        backgroundColor: selectedMachines.includes(machineId)
                          ? "rgba(25, 118, 210, 0.08)"
                          : "transparent",
                      }}
                    />
                  ))}
                </FormGroup>
              </Paper>
            </Box>
          </Collapse>
          <M.InnerContainerWithScroll ref={containerWidthRef}>
            {viewMode === "graph" ? (
              <>
                {filteredData && Object.keys(filteredData).length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    {sortedMachineEntries.map((machineEntry, index) => {
                      const {
                        machineId,
                        loadDataArray,
                        machineName,
                        baseLoad,
                        maxLoad,
                        statistics,
                      } = machineEntry;
                      return (
                        <Box
                          key={machineId}
                          sx={{
                            width: "calc(100% - 2rem)",
                            boxSizing: "border-box",
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                            margin: "1rem 0 0 1rem",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              padding: "10px 20px",
                              fontWeight: "bold",
                              borderBottom: "1px solid #f0f0f0",
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "12px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontStyle: "italic",
                                  fontSize: "16px",
                                  color: "#333",
                                  marginRight: "16px",
                                }}
                              >
                                {machineName}
                              </Typography>
                              <Tooltip title="장비 가동시간">
                                <Chip
                                  label={`총 가동시간: ${statistics.totalOperationTime}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Tooltip>
                              <Tooltip title="'기준 부하율'을 초과해서 '허용 최대 부하율'까지 가동되는 시간">
                                <Chip
                                  label={`과부하 허용시간: ${statistics.allowedOverloadTime}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              </Tooltip>
                              <Tooltip title="'허용 최대 부하율' 이상의 허용 불가 상태 시간">
                                <Chip
                                  label={`과부하 불가시간: ${statistics.prohibitedOverloadTime}`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                />
                              </Tooltip>
                              <Tooltip title="총 가동 시간 중 '기준 부하율' 초과 ~ '허용 최대 부하율' 범위에 있는 시간 비율">
                                <Chip
                                  label={`과부하 시간 비율: ${statistics.overloadRatio}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              </Tooltip>
                              {baseLoad !== null && maxLoad !== null && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  (
                                  <span
                                    style={{
                                      borderBottom: "2px dashed #2ecc71",
                                      paddingBottom: "2px",
                                    }}
                                  >
                                    기준 부하율: {baseLoad}%
                                  </span>
                                  ,{" "}
                                  <span
                                    style={{
                                      borderBottom: "2px dashed #e74c3c",
                                      paddingBottom: "2px",
                                    }}
                                  >
                                    최대 부하율: {maxLoad}%
                                  </span>
                                  )
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <div
                            ref={scrollContainersRef.current[index]}
                            style={{
                              width: "100%",
                              height: "110px",
                              overflowX: "auto",
                              overflowY: "hidden",
                            }}
                            className="chart-scroll-container"
                            onScroll={(e) => handleScroll(e, index)}
                          >
                            <div style={{ width: chartWidth, height: "100%" }}>
                              <Charts
                                loadData={loadDataArray}
                                timeUnitMinutes={getTimeUnitMinutes()}
                                barWidth={barWidth}
                                baseLoad={baseLoad}
                                maxLoad={maxLoad}
                                showAboveBaseLoad={
                                  filterOptions.showAboveBaseLoad
                                }
                              />
                            </div>
                          </div>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  !loading && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "400px",
                        width: "100%",
                      }}
                    >
                      <Typography variant="h6" color="textSecondary">
                        표시할 데이터가 없습니다.
                      </Typography>
                    </Box>
                  )
                )}
                {selectedMachines.length === 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "400px",
                      width: "100%",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography variant="h6" color="textSecondary">
                      선택된 장비가 없습니다.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={toggleAllMachines}
                    >
                      모든 장비 선택
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <GanttIFframe
                title="gantt"
                width="100%"
                height="100%"
                src={`${PLAN_URL}/plan_graph_gantt?group_code=${group_code}&project_code=${project_code}`}
                allow="cross-origin-isolated; accelerometer; gyroscope;"
              />
            )}
            {loading && (
              <M.DimBox>
                <M.DimContent>
                  <CirclesWithBar
                    visible={true}
                    height="100"
                    width="100"
                    color="#ffffff"
                    outerCircleColor="#ffffff"
                    innerCircleColor="#ffffff"
                    barColor="#4fa94d"
                    ariaLabel="circles-with-bar-loading"
                  />
                  <Typography variant="h6" color="white">
                    {loadingMessage}
                  </Typography>
                </M.DimContent>
              </M.DimBox>
            )}
          </M.InnerContainerWithScroll>
        </M.FrameContainer>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={2000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </M.Wrapper>
    </LocalizationProvider>
  );
}

export default Graph;
